import { Loader2, Mic, Square } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VoiceDictationHandlers } from "@/hooks/useVoiceDictation";
import { useVoiceInput } from "@/hooks/useVoiceInput";

export interface VoiceInputButtonProps {
  /**
   * Called **once** with the final transcript when a recording session ends.
   * The consumer typically appends it to their input state. Use this for the
   * simple "commit on stop" mode.
   *
   * Ignored when `dictation` is provided (live-fill mode drives the input
   * directly and would double-write).
   */
  onTranscript?: (transcript: string) => void;
  /**
   * Live "fill as you speak" handlers from `useVoiceDictation`. When provided,
   * the input fills in incrementally as the user speaks (and `onTranscript` is
   * not used). Spread the hook's return value here.
   */
  dictation?: VoiceDictationHandlers;
  /** Disable the button (e.g. while streaming a response). */
  disabled?: boolean;
  /** Additional Tailwind classes for the outer button. */
  className?: string;
  /** Button size variant. */
  size?: "sm" | "md";
  /** Optional visible text label displayed next to the mic icon. */
  label?: string;
}

/** How long the error tooltip stays visible (ms). */
const ERROR_DISPLAY_MS = 5000;

/** Number of bars in the waveform visualizer. */
const WAVEFORM_BAR_COUNT = 5;

/**
 * Format elapsed seconds as mm:ss.
 */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Microphone toggle button with backend-powered speech-to-text.
 *
 * Records audio via MediaRecorder and sends it to the backend Whisper
 * endpoint for transcription.  Works reliably across all modern browsers
 * (Chrome, Edge, Firefox, Safari, Arc).
 *
 * When recording, expands into a dedicated recording panel showing:
 * - Real-time audio waveform visualization (responds to voice volume)
 * - Elapsed recording time
 * - Live status indicator ("Listening..." / "Transcribing...")
 * - A prominent stop button
 *
 * Renders nothing if the browser doesn't support MediaRecorder so the
 * UI degrades gracefully on very old browsers.
 */
export function VoiceInputButton({
  onTranscript,
  dictation,
  disabled = false,
  className = "",
  size = "md",
  label,
}: Readonly<VoiceInputButtonProps>) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-dismiss error after a timeout.
  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(null), ERROR_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const handleError = useCallback((error: string) => {
    setErrorMessage(error);
  }, []);

  // ── "Commit on stop" mode (no live-fill) ──
  // Buffer the latest full session transcript and fire onTranscript once when
  // the session ends. Keeps the simple consumer contract (called once with the
  // final text) even though the hook now streams the full transcript.
  const latestTranscriptRef = useRef("");

  const handleTranscript = useCallback((sessionTranscript: string) => {
    latestTranscriptRef.current = sessionTranscript;
  }, []);

  const handleEnd = useCallback(() => {
    const text = latestTranscriptRef.current.trim();
    latestTranscriptRef.current = "";
    if (text) onTranscript?.(text);
  }, [onTranscript]);

  const handleStart = useCallback(() => {
    latestTranscriptRef.current = "";
  }, []);

  // Live-fill mode drives the input directly via the dictation handlers;
  // otherwise use the commit-on-stop handlers above.
  const { isSupported, isListening, isTranscribing, audioLevel, elapsedSeconds, toggle } = useVoiceInput({
    continuous: true,
    onStart: dictation ? dictation.onStart : handleStart,
    onEnd: dictation ? dictation.onEnd : handleEnd,
    onTranscript: dictation ? dictation.onTranscript : handleTranscript,
    onError: handleError,
  });

  // Generate waveform bar heights based on audio level with some variation.
  const barHeights = useMemo(() => {
    const bars: number[] = [];
    for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
      // Create slight variation between bars for a natural look.
      // Middle bars are taller, edge bars shorter.
      const position = Math.abs(i - (WAVEFORM_BAR_COUNT - 1) / 2) / ((WAVEFORM_BAR_COUNT - 1) / 2);
      const variation = 1 - position * 0.4;
      // Map audio level (0–1) to bar height (min 15%, max 100%).
      const height = Math.max(0.15, Math.min(1, audioLevel * 3 * variation));
      bars.push(height);
    }
    return bars;
  }, [audioLevel]);

  // Don't render anything in unsupported browsers.
  if (!isSupported) return null;

  const sizeClasses = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const iconSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";

  // ── Active recording panel ──
  if (isListening || (isTranscribing && !isListening)) {
    return (
      <div className="flex items-center gap-2">
        {/* Recording panel */}
        <div className="flex items-center gap-3 px-3 py-2 bg-red-950/40 border border-red-700/40 rounded-lg animate-in fade-in slide-in-from-left-2 duration-200">
          {/* Waveform visualizer */}
          <div className="flex items-center gap-0.5 h-6 w-10">
            {isListening ? (
              barHeights.map((height, i) => (
                <div
                  key={`bar-${i}`} // NOSONAR — index key safe for transient audio visualizer bars; list is rebuilt every animation frame
                  className="w-1.5 rounded-full bg-red-400 transition-all duration-75"
                  style={{
                    height: `${Math.round(height * 100)}%`,
                    minHeight: "4px",
                    opacity: 0.6 + height * 0.4,
                  }}
                />
              ))
            ) : (
              /* Transcribing spinner when stopped but still processing */
              <Loader2 className="w-5 h-5 text-red-400 animate-spin" />
            )}
          </div>

          {/* Status and timer */}
          <div className="flex flex-col items-start min-w-0">
            <span className="text-[11px] font-medium text-red-300 leading-tight">
              {(() => {
                if (isListening) return isTranscribing ? "Transcribing…" : "Listening…";
                return "Finishing…";
              })()}
            </span>
            {isListening && (
              <span className="text-[10px] text-red-400/70 font-mono leading-tight">
                {formatDuration(elapsedSeconds)}
              </span>
            )}
          </div>

          {/* Stop button */}
          <button
            type="button"
            onClick={toggle}
            disabled={isTranscribing && !isListening}
            title="Stop recording"
            aria-label="Stop recording"
            className="flex items-center justify-center w-7 h-7 bg-red-600 hover:bg-red-700 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isListening ? (
              <Square className="w-3 h-3 text-white fill-white" />
            ) : (
              <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
            )}
          </button>
        </div>

        {/* Error tooltip */}
        {errorMessage && (
          <div className="px-3 py-2 bg-red-900/90 text-red-200 text-xs rounded-lg shadow-lg max-w-[260px] whitespace-normal border border-red-700/50">
            <p>{errorMessage}</p>
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="mt-1 text-red-400 hover:text-red-300 text-[10px] underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Idle state — simple mic button ──
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setErrorMessage(null);
          toggle();
        }}
        disabled={disabled}
        title="Start voice input"
        aria-label="Start voice input"
        className={`
          flex items-center justify-center rounded-lg transition
          ${label ? "gap-2 px-3" : sizeClasses}
          ${label ? "h-10" : ""}
          bg-surface-secondary hover:bg-interactive-active border border-border-secondary
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `.trim()}
      >
        <Mic className={`${iconSize} text-content-tertiary`} />
        {label && <span className="text-sm text-content-tertiary">{label}</span>}
      </button>

      {/* Error tooltip */}
      {errorMessage && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-red-900/90 text-red-200 text-xs rounded-lg shadow-lg max-w-[260px] whitespace-normal border border-red-700/50">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="mt-1 text-red-400 hover:text-red-300 text-[10px] underline"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
